import * as React from 'react';
import { Upload } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import type { InvoiceDeliveryVoucher } from 'contracts/DeliveryVouchers';
import type { RootState } from 'store';
import { CreateDeliveryVoucherActions } from 'store/ducks/deliveryVouchers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

type Ref = {
  open: (invoice: InvoiceDeliveryVoucher) => void;
  close: () => void;
};

type Props = {
  onSuccess: () => void;
};

export type UploadDeliveryVoucherDialogRef = Ref;

const ACCEPTED_VOUCHER_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';

export const UploadDeliveryVoucherDialog = React.forwardRef<Ref, Props>(
  ({ onSuccess }, ref) => {
    const dispatch = useDispatch();
    const inputId = React.useId();
    const [open, setOpen] = React.useState(false);
    const [invoice, setInvoice] = React.useState<InvoiceDeliveryVoucher>();
    const [file, setFile] = React.useState<File | null>(null);
    const creating = useSelector(
      (state: RootState) => state.createDeliveryVoucher.loading
    );

    const closeDialog = React.useCallback(() => {
      setOpen(false);
      setInvoice(undefined);
      setFile(null);
    }, []);

    const onOpenChange = React.useCallback(
      (nextOpen: boolean) => {
        // Evita fechar o dialog enquanto a saga de upload ainda está processando.
        if (!nextOpen && creating) return;
        if (!nextOpen) return closeDialog();

        setOpen(true);
      },
      [closeDialog, creating]
    );

    const handleFileChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setFile(event.target.files?.[0] ?? null);
      },
      []
    );

    const handleSuccess = React.useCallback(() => {
      // Fecha o dialog antes de atualizar a listagem e os indicadores da página.
      closeDialog();
      onSuccess();
    }, [closeDialog, onSuccess]);

    const handleUpload = React.useCallback(() => {
      if (!file || !invoice?.nfeId) return;

      // Mantém o payload esperado pela API de canhotos e pela saga já existente.
      dispatch(
        CreateDeliveryVoucherActions.request(
          {
            image: file,
            origin: 'MANUAL',
            chave_nf: invoice.nfeId,
          },
          handleSuccess
        )
      );
    }, [dispatch, file, handleSuccess, invoice?.nfeId]);

    React.useImperativeHandle(
      ref,
      () => ({
        // A linha da tabela informa qual nota receberá o novo comprovante.
        open: selectedInvoice => {
          setInvoice(selectedInvoice);
          setFile(null);
          setOpen(true);
        },
        close: closeDialog,
      }),
      [closeDialog]
    );

    const uploadDisabled = !file || !invoice?.nfeId || creating;

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-full max-w-[540px]">
          <DialogHeader>
            <DialogTitle>Inserir comprovante</DialogTitle>
            <DialogDescription>
              Selecione o comprovante da NF {invoice?.infnfeIdeNnf}-
              {invoice?.infnfeIdeSerie}.
            </DialogDescription>
          </DialogHeader>

          <label
            htmlFor={inputId}
            className="flex min-h-[96px] cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-4 py-5 text-center text-sm text-slate-500 transition hover:bg-slate-50"
          >
            <Upload className="h-5 w-5" />
            <span className="max-w-full break-all">
              {file?.name ?? 'Selecione um arquivo'}
            </span>
            <input
              id={inputId}
              type="file"
              className="hidden"
              disabled={creating}
              accept={ACCEPTED_VOUCHER_FILE_TYPES}
              onChange={handleFileChange}
            />
          </label>

          {!invoice?.nfeId && (
            <p className="text-sm text-red-600">
              A nota não possui uma chave disponível para o envio.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Button.Root
              type="button"
              size="sm"
              variant="secondary"
              disabled={creating}
              onClick={closeDialog}
            >
              Cancelar
            </Button.Root>
            <Button.Root
              type="button"
              size="sm"
              disabled={uploadDisabled}
              onClick={handleUpload}
            >
              {creating ? <ActivityIndicator /> : 'Enviar'}
            </Button.Root>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

UploadDeliveryVoucherDialog.displayName = 'UploadDeliveryVoucherDialog';
